'use client';

import { CookieConsentStatus } from '@hub/common/src/analytics-utils';
import { HOST_ERROR_SEGMENTS } from '@hub/common/src/constants';

import { useCitOriginClient } from '@hub/common-components/src/CitOriginClientProvider';
import { useCookieConsentStatus } from './CookieConsentStatusProvider';
import { usePathname, useSelectedLayoutSegment } from 'next/navigation';
import Link from 'next/link';
import { CitOrigin } from '@hub/common/src/types/cit-public-api';

export default function CookieBanner() {
  const citOrigin = useCitOriginClient();
  const pathname = usePathname();
  // the rewritten route, since the visible url on a host with no account is whatever was typed
  const segment = useSelectedLayoutSegment();
  const isHostError = citOrigin === 'hubpro' && !!segment && HOST_ERROR_SEGMENTS.includes(segment);

  const { hasConsent, setHasConsent, consentLoaded } = useCookieConsentStatus();

  const handleAccept = () => {
    setHasConsent(CookieConsentStatus.GRANTED);
  };

  const handleDeny = () => {
    setHasConsent(CookieConsentStatus.DENIED);
  };

  // don't show banner if already accepted / denied, or loading consent. A host with no account
  // tracks nothing, so there is nothing to consent to
  if (
    !consentLoaded ||
    hasConsent !== CookieConsentStatus.PENDING ||
    pathname === '/cookies' ||
    isHostError
  ) {
    return null;
  }

  // rendered beside #root by the root layouts, so the inert modals put on #root don't reach it.
  // z-[1010] because it needs to be above the MediaViewer thumnails (z-100) and ReferenceOverlay (z-[1000]) but below the lightGallery light box (z-[1040])
  return (
    <div className='fixed bottom-0 left-0 right-0 z-[1010] px-4 pb-4'>
      <div
        className='mx-auto flex max-w-6xl flex-col items-stretch gap-5 rounded-xl border border-gray-400 bg-white p-3 shadow-lg sm:flex-row sm:items-center sm:gap-6'
        role='region'
        aria-label='Cookie consent'
      >
        <p className='flex-1 text-xs text-gray-900 sm:text-sm'>
          {`${citOrigin === CitOrigin.HUB ? 'CatalogIt HUB uses' : 'We use'} optional cookies to measure site usage and improve the user
          experience. ${citOrigin === CitOrigin.HUB ? 'HUB page owners may access analytics related to activity on their pages.' : ''}`}
          You may change your cookie settings at any time by visiting the{' '}
          <Link className='text-blue-500 hover:text-blue-600' href='/cookies'>
            cookie settings page
          </Link>{' '}
          located in the footer of the home page.
        </p>
        <div className='flex shrink-0 flex-wrap items-center justify-end gap-3'>
          <button
            type='button'
            className='rounded-md cursor-pointer hover:bg-gray-100 border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900'
            onClick={handleDeny}
          >
            Reject All
          </button>
          <button
            type='button'
            className='rounded-md cursor-pointer bg-[#005F91] px-4 py-2 text-sm font-medium text-white transition hover:brightness-90'
            onClick={handleAccept}
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
